import { memo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  ArrowUpDown,
  ArrowUpRight,
  Check,
  ChevronDown,
  ChevronUp,
  Clock,
  Hourglass,
  Loader2,
  Mic,
  MicOff,
  Pencil,
  Plus,
  RefreshCw,
  Repeat,
  Sparkles,
  StickyNote,
  Tag as TagIcon,
  Trash2,
  X,
} from 'lucide-react';
import { useStore, isPremium } from '../store/useStore';
import { ZONES, type Task } from '../types';
import { hintFromPicker, quickDateOptions } from '../engine/dates';
import { readyToComplete, stepProgress, taskProgress } from '../engine/progress';
import { suggestTagName, TAG_KINDS, type TagKind } from '../engine/tags';
import { moveAvailability, orderGroupLabel, type MoveAvailability } from '../engine/manualOrder';
import { ProgressCircle } from './ProgressCircle';
import { ReportResultLink } from './ReportResultLink';
import { REPEAT_OPTIONS, repeatBadge } from '../engine/recurrence';
import { AddToCalendar } from './AddToCalendar';
import { CoreGuidance, CoreStar } from './insights/CoreStar';
import { HandOffPanel } from './insights/HandOffPanel';
import { ZoneIcon } from './ZoneIcon';
import { isNative } from '../native/native';
import { useDictation } from '../hooks/useDictation';

/**
 * A single task row with premium drill-down expansion.
 * Memoized: only the card whose task object changed re-renders, which keeps
 * long zone lists cheap during step toggles and quick-adds.
 */
export const TaskCard = memo(function TaskCard({ task }: { task: Task }) {
  const drillDown = useStore((s) => s.drillDown);
  const addExtraStep = useStore((s) => s.addExtraStep);
  const planNotice = useStore((s) => (s.planNotice?.taskId === task.id ? s.planNotice.text : undefined));
  const updatePlan = useStore((s) => s.updatePlan);
  const addManualStep = useStore((s) => s.addManualStep);
  const updateStep = useStore((s) => s.updateStep);
  const updateTaskText = useStore((s) => s.updateTaskText);
  const updateTaskDeadline = useStore((s) => s.updateTaskDeadline);
  const setTaskRepeat = useStore((s) => s.setTaskRepeat);
  const deleteStep = useStore((s) => s.deleteStep);
  const moveStep = useStore((s) => s.moveStep);
  const promoteStep = useStore((s) => s.promoteStep);
  const toggleTaskDone = useStore((s) => s.toggleTaskDone);
  const cycleTaskState = useStore((s) => s.cycleTaskState);
  const cycleStepState = useStore((s) => s.cycleStepState);
  const updateTaskNote = useStore((s) => s.updateTaskNote);
  const allTags = useStore((s) => s.tags);
  const toggleTaskTag = useStore((s) => s.toggleTaskTag);
  const createTag = useStore((s) => s.createTag);
  const moveTask = useStore((s) => s.moveTask);
  const moveTaskInZone = useStore((s) => s.moveTaskInZone);
  const setSortMode = useStore((s) => s.setSortMode);
  /** Is the board already in Date sort, where hand order shows? (A saved
   *  Project / Role sort with no tags counts as Priority — MatrixScreen's
   *  effectiveSort.) Decides what the Order-in-zone note says. */
  const inDateSort = useStore((s) => s.sortMode === 'date');
  /** Reordering is a DATE-view concept ("undated below, date order
   *  priority"). Under tag/priority sorts the move was invisible (Chad,
   *  12 Aug: "not working") — so moving snaps the board to Date sort,
   *  same pattern as the focus button (#15). */
  const liftShift = (direction: -1 | 1) => {
    setSortMode('date');
    moveTaskInZone(task.id, direction);
  };
  const deleteTask = useStore((s) => s.deleteTask);
  const expandingTaskId = useStore((s) => s.expandingTaskId);
  const subscription = useStore((s) => s.subscription);

  const freePlansRemaining = useStore((s) => s.freePlansRemaining);

  const [expanded, setExpanded] = useState(false);
  const [moveOpen, setMoveOpen] = useState(false);
  /** Which of Move up / Move down can act, and why not (12 Oct 2026: a
   *  lone undated task's Move up did nothing, silently). A primitive key,
   *  computed only while this card's picker is open, so closed cards never
   *  re-render on other tasks' edits. */
  const movesKey = useStore((s) =>
    moveOpen
      ? JSON.stringify(
          moveAvailability(
            Object.values(s.tasks).filter((t) => t.zone === task.zone),
            task.id,
          ),
        )
      : '',
  );
  const moves: MoveAvailability = movesKey
    ? (JSON.parse(movesKey) as MoveAvailability)
    : { up: true, down: true, groupSize: 0, reason: null };
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState('');
  const [editingDate, setEditingDate] = useState(false);
  const [dateDraft, setDateDraft] = useState('');
  const [pickDate, setPickDate] = useState('');
  const [pickTime, setPickTime] = useState('');
  const [showTime, setShowTime] = useState(false);
  const [editingStepId, setEditingStepId] = useState<string | null>(null);
  const [stepDraft, setStepDraft] = useState('');
  const [newStep, setNewStep] = useState('');
  const stepVoice = useDictation(setNewStep);
  const [noteOpen, setNoteOpen] = useState(false);
  const [noteDraft, setNoteDraft] = useState('');
  const [tagsOpen, setTagsOpen] = useState(false);
  const tagsRef = useRef<HTMLDivElement | null>(null);
  /** One-tap tag assignment from the COLLAPSED card (V2 §4c, register #26):
   *  expand, open the picker, and bring it into view — no hunting. */
  const openTagPicker = () => {
    setExpanded(true);
    setTagsOpen(true);
    setTimeout(
      () => tagsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }),
      350, // let the expand animation land first
    );
  };
  const [newTagName, setNewTagName] = useState('');
  const [newTagKind, setNewTagKind] = useState<TagKind>('project');
  const [updateOpen, setUpdateOpen] = useState(false);
  const [updateNote, setUpdateNote] = useState('');
  const [updateBusy, setUpdateBusy] = useState(false);
  const noteVoice = useDictation(setUpdateNote);

  const submitNewStep = () => {
    if (!newStep.trim()) return;
    addManualStep(task.id, newStep);
    setNewStep('');
  };

  const submitPlanUpdate = async () => {
    if (!updateNote.trim() || updateBusy) return;
    setUpdateBusy(true);
    try {
      await updatePlan(task.id, updateNote);
      setUpdateNote('');
      setUpdateOpen(false);
    } finally {
      setUpdateBusy(false);
    }
  };

  const saveStepEdit = () => {
    if (editingStepId && stepDraft.trim()) updateStep(task.id, editingStepId, stepDraft);
    setEditingStepId(null);
  };

  const saveTitleEdit = () => {
    if (titleDraft.trim()) updateTaskText(task.id, titleDraft);
    setEditingTitle(false);
  };
  const premium = isPremium(subscription);
  const expanding = expandingTaskId === task.id;
  /** Free users sample their first plans in full before any paywall. */
  const canGenerate = premium || freePlansRemaining > 0;

  /** Tapping the task body only expands/collapses. AI generation happens
   *  ONLY via the explicit "Tap for AI action plan" control — a stray tap
   *  (e.g. aiming for the date chip) must never spend an AI plan. */
  const handleTap = () => setExpanded((e) => !e);

  const startDateEdit = () => {
    setDateDraft(task.deadlineHint ?? '');
    setPickDate('');
    setPickTime('');
    setShowTime(false);
    setEditingDate(true);
  };

  const closeDateEdit = () => {
    setEditingDate(false);
    setShowTime(false);
  };

  const saveDateEdit = () => {
    // Picker wins when used; otherwise free text (no guessed timestamp).
    if (pickDate) {
      const parsed = hintFromPicker(pickDate, showTime && pickTime ? pickTime : undefined);
      if (parsed) updateTaskDeadline(task.id, parsed.hint, parsed.at);
    } else {
      updateTaskDeadline(task.id, dateDraft);
    }
    closeDateEdit();
  };

  const totalMinutes = task.steps.reduce((sum, s) => sum + s.minutes, 0);
  const doneSteps = task.steps.filter((s) => s.done).length;

  return (
    <div
      className={`rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/60 transition-colors ${
        task.done ? 'opacity-50' : 'hover:bg-slate-50 dark:hover:bg-slate-900'
      }`}
    >
      <div className="flex items-start gap-2 p-3">
        <button
          onClick={() => cycleTaskState(task.id)}
          aria-label={
            task.done
              ? 'Reset task'
              : task.startedAt || task.steps.some((st) => st.done || st.startedAt)
                ? 'Mark done'
                : 'Mark in progress'
          }
          title="Tap: not started → in progress → done"
          className="mt-0.5 shrink-0 text-slate-400 dark:text-slate-600 transition-colors hover:text-emerald-500 dark:hover:text-emerald-400"
        >
          <ProgressCircle progress={taskProgress(task)} done={task.done} size={18} />
        </button>

        {editingTitle ? (
          /* Title edit: fix scan/OCR spelling without recreating the task */
          <div className="flex flex-1 items-center gap-1.5">
            <input
              type="text"
              value={titleDraft}
              onChange={(e) => setTitleDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') saveTitleEdit();
                if (e.key === 'Escape') setEditingTitle(false);
              }}
              autoFocus
              aria-label="Edit task text"
              className="min-w-0 flex-1 rounded-md border border-brand-300 dark:border-brand-500/40 bg-white dark:bg-slate-900 px-2 py-1.5 text-xs font-semibold text-slate-900 dark:text-white focus:border-brand-500 focus:outline-none"
            />
            <button
              onClick={saveTitleEdit}
              aria-label="Save task text"
              className="shrink-0 rounded-md bg-brand-600 p-1.5 text-white hover:bg-brand-500"
            >
              <Check size={13} />
            </button>
            <button
              onClick={() => setEditingTitle(false)}
              aria-label="Cancel edit"
              className="shrink-0 rounded-md border border-slate-200 dark:border-slate-800 p-1.5 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"
            >
              <X size={13} />
            </button>
          </div>
        ) : (
        <button onClick={handleTap} className="flex-1 text-left">
          <p
            className={`text-xs font-semibold text-slate-800 dark:text-slate-200 ${task.done ? 'line-through' : ''}`}
          >
            {task.text}
          </p>
          <span className="mt-1 flex flex-wrap items-center gap-2">
            {task.rolloverCount > 0 && (
              <span className="inline-flex items-center gap-1 rounded bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-black uppercase text-amber-600 dark:text-amber-400">
                <Hourglass size={9} /> Roll ×{task.rolloverCount}
              </span>
            )}
            {task.deadlineHint ? (
              <span
                role="button"
                tabIndex={0}
                title="Edit or clear this date"
                onClick={(e) => {
                  e.stopPropagation();
                  startDateEdit();
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.stopPropagation();
                    startDateEdit();
                  }
                }}
                className="inline-flex items-center gap-1 rounded-full border border-red-200 dark:border-red-500/20 bg-red-500/10 px-2 py-0.5 text-[11px] font-semibold text-red-600 dark:text-red-400 hover:bg-red-500/20"
              >
                <Clock size={10} /> {task.deadlineHint}
                {repeatBadge(task.repeat) && (
                  <span className="ml-0.5 inline-flex items-center gap-0.5" aria-label={`Repeats: ${repeatBadge(task.repeat)}`}>
                    · <Repeat size={9} /> {repeatBadge(task.repeat)}
                  </span>
                )}
              </span>
            ) : (
              !task.done && (
                <span
                  role="button"
                  tabIndex={0}
                  title="Set a target date"
                  onClick={(e) => {
                    e.stopPropagation();
                    startDateEdit();
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.stopPropagation();
                      startDateEdit();
                    }
                  }}
                  /* Register #32 (Shirann): the ghost chip was invisible — a soft
                     emerald outline says "action available" without shouting
                     (red stays reserved for dates that EXIST). */
                  className="inline-flex items-center gap-1 rounded-full border border-emerald-300 dark:border-emerald-500/40 bg-emerald-50/60 dark:bg-emerald-950/20 px-2 py-0.5 text-[10px] font-semibold text-emerald-700 dark:text-emerald-400 hover:border-emerald-400 hover:bg-emerald-100 dark:hover:bg-emerald-900/30"
                >
                  <Clock size={9} /> Add date
                </span>
              )
            )}
            {!task.stepsGenerated && !task.done && (
              <>
                <span
                  role="button"
                  tabIndex={0}
                  onClick={(e) => {
                    e.stopPropagation();
                    void drillDown(task.id); // opens paywall only when samples are spent
                    if (canGenerate) setExpanded(true);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.stopPropagation();
                      void drillDown(task.id);
                      if (canGenerate) setExpanded(true);
                    }
                  }}
                  className="inline-flex items-center gap-1 text-[11px] font-medium text-brand-600 dark:text-brand-400 hover:underline underline-offset-2"
                >
                  <Sparkles size={10} />
                  {premium
                    ? 'Tap for AI action plan'
                    : freePlansRemaining > 0
                      ? `Tap for AI action plan · ${freePlansRemaining} free left`
                      : 'Unlock AI action plans'}
                </span>
                <span
                  role="button"
                  tabIndex={0}
                  onClick={(e) => {
                    e.stopPropagation();
                    setExpanded(true);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.stopPropagation();
                      setExpanded(true);
                    }
                  }}
                  className="text-[11px] font-medium text-slate-500 underline-offset-2 hover:underline"
                >
                  or write your own
                </span>
              </>
            )}
            {(task.tags ?? []).map((tid) => {
              const tag = allTags[tid];
              if (!tag) return null;
              return (
                <span
                  key={tid}
                  role="button"
                  tabIndex={0}
                  title="Change project / role"
                  onClick={(e) => {
                    e.stopPropagation();
                    openTagPicker();
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.stopPropagation();
                      openTagPicker();
                    }
                  }}
                  className="inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-[10px] font-bold"
                  style={{
                    color: tag.color,
                    borderColor: `${tag.color}55`,
                    backgroundColor: `${tag.color}14`,
                  }}
                >
                  <span
                    className="h-1.5 w-1.5 rounded-full"
                    style={{ backgroundColor: tag.color }}
                  />
                  {tag.name}
                </span>
              );
            })}
            {(task.tags ?? []).length === 0 && !task.done && (
              /* ＋Tag ghost chip (V2 §4c, register #26): project assignment
                 one tap from the collapsed card, beside Add date. */
              <span
                role="button"
                tabIndex={0}
                title="Add to a project / role"
                onClick={(e) => {
                  e.stopPropagation();
                  openTagPicker();
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.stopPropagation();
                    openTagPicker();
                  }
                }}
                /* Register #32b (Chad): tags are critical to optimal use — the
                   ghost chip was lost. Soft indigo pairs with the emerald date
                   chip: two visible assignment axes, two colours. */
                className="inline-flex items-center gap-1 rounded-full border border-indigo-300 dark:border-indigo-500/40 bg-indigo-50/60 dark:bg-indigo-950/20 px-2 py-0.5 text-[10px] font-semibold text-indigo-600 dark:text-indigo-400 hover:border-indigo-400 hover:bg-indigo-100 dark:hover:bg-indigo-900/30"
              >
                <TagIcon size={9} /> Tag
              </span>
            )}
            {task.steps.length > 0 && (
              <span className="text-[11px] font-medium text-slate-500">
                {doneSteps}/{task.steps.length} steps · ~{totalMinutes} min
              </span>
            )}
            {readyToComplete(task) && (
              <span
                role="button"
                tabIndex={0}
                onClick={(e) => {
                  e.stopPropagation();
                  toggleTaskDone(task.id);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.stopPropagation();
                    toggleTaskDone(task.id);
                  }
                }}
                className="inline-flex items-center gap-1 rounded-full border border-emerald-300 dark:border-emerald-500/30 bg-emerald-500/10 px-2 py-0.5 text-[11px] font-bold text-emerald-700 dark:text-emerald-400 hover:bg-emerald-500/20"
              >
                All steps done — mark complete?
              </span>
            )}
          </span>
        </button>
        )}

        {!editingTitle && (
        <button
          onClick={() => {
            setTitleDraft(task.text);
            setEditingTitle(true);
          }}
          aria-label="Edit task text"
          title="Edit task text"
          className="mt-0.5 shrink-0 rounded-md p-0.5 text-slate-400 dark:text-slate-600 transition-colors hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-brand-600 dark:hover:text-brand-400"
        >
          <Pencil size={13} />
        </button>
        )}

        {!editingTitle && (
        <button
          onClick={() => setMoveOpen((m) => !m)}
          aria-label="Move to another zone"
          aria-expanded={moveOpen}
          title="Move to another zone"
          className={`mt-0.5 shrink-0 rounded-md p-0.5 transition-colors hover:bg-brand-50 dark:hover:bg-brand-900/20 ${
            moveOpen
              ? 'bg-brand-600/10 text-brand-600 dark:text-brand-400'
              : 'text-brand-500 dark:text-brand-400 hover:text-brand-600 dark:hover:text-brand-300'
          }`}
        >
          <ArrowUpDown size={15} />
        </button>
        )}

        {!editingTitle && task.steps.length > 0 && (
          <button
            onClick={() => setExpanded((e) => !e)}
            aria-label={expanded ? 'Collapse action plan' : 'Expand action plan'}
            aria-expanded={expanded}
            className="mt-0.5 shrink-0 rounded-md p-0.5 text-slate-500 transition-colors hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-brand-600 dark:hover:text-brand-400"
          >
            <ChevronDown
              size={16}
              className={`transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`}
            />
          </button>
        )}

        {!editingTitle && (
        <button
          onClick={() => deleteTask(task.id)}
          aria-label="Delete task"
          className="mt-0.5 shrink-0 text-slate-400 dark:text-slate-600 transition-colors hover:text-red-500 dark:hover:text-red-400"
        >
          <Trash2 size={15} />
        </button>
        )}
      </div>

      {/* Target-date editor — three optional layers, precision never
          demanded: one-tap chips → native calendar → time only on request.
          Free text stays first-class (vagueness is a feature). */}
      {editingDate && (
        <div className="space-y-2 border-t border-slate-200 dark:border-slate-800 px-3 py-2">
          {/* Layer 1: one-tap chips (saves immediately) */}
          <div className="flex flex-wrap gap-1.5">
            {quickDateOptions().map((opt) => (
              <button
                key={opt.label}
                onClick={() => {
                  updateTaskDeadline(task.id, opt.hint, opt.at);
                  closeDateEdit();
                }}
                className="rounded-full border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-950 px-2.5 py-1 text-[11px] font-bold text-slate-600 dark:text-slate-300 transition-colors hover:border-red-300 hover:text-red-600 dark:hover:text-red-400"
              >
                {opt.label}
              </button>
            ))}
          </div>

          {/* Repeat (18 Oct 2026, engine/recurrence.ts): ticking a repeating
              task done creates the next one, dated by the rule. The SETTING
              is web-only until web users have tried it (Chad, 18 Oct); a
              repeating task synced to a phone keeps its badge and behaviour. */}
          {!isNative() && (
          <div className="flex flex-wrap items-center gap-1.5" role="group" aria-label="Repeat">
            <span className="flex items-center gap-1 text-[11px] font-bold text-slate-600 dark:text-slate-300">
              <Repeat size={11} /> Repeat
            </span>
            {[{ id: undefined, label: 'Never' }, ...REPEAT_OPTIONS].map((o) => {
              const on = (task.repeat ?? undefined) === o.id;
              return (
                <button
                  key={o.label}
                  type="button"
                  aria-pressed={on}
                  onClick={() => setTaskRepeat(task.id, o.id)}
                  className={`rounded-full border px-2 py-0.5 text-[11px] font-bold transition-colors ${
                    on
                      ? 'border-brand-500 bg-brand-500/10 text-brand-700 dark:text-brand-300'
                      : 'border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:border-brand-300'
                  }`}
                >
                  {o.label}
                </button>
              );
            })}
          </div>
          )}

          {/* Calendar export (web first): the task goes out to the user's
              own calendar; ZoneIn never reads one. */}
          <AddToCalendar task={task} />

          {/* Layer 2 + 3: native calendar, time only on request */}
          <div className="flex flex-wrap items-center gap-1.5">
            <input
              type="date"
              value={pickDate}
              onChange={(e) => setPickDate(e.target.value)}
              aria-label="Pick a date"
              className="rounded-md border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-2 py-1 text-xs text-slate-900 dark:text-white focus:border-red-400 focus:outline-none"
            />
            {pickDate &&
              (showTime ? (
                <input
                  type="time"
                  value={pickTime}
                  onChange={(e) => setPickTime(e.target.value)}
                  aria-label="Pick a time (optional)"
                  autoFocus
                  className="rounded-md border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-2 py-1 text-xs text-slate-900 dark:text-white focus:border-red-400 focus:outline-none"
                />
              ) : (
                <button
                  onClick={() => setShowTime(true)}
                  className="rounded-md border border-dashed border-slate-300 dark:border-slate-700 px-2 py-1 text-[11px] font-medium text-slate-500 hover:border-red-300 hover:text-red-500"
                >
                  + time
                </button>
              ))}
            <button
              onClick={saveDateEdit}
              disabled={!pickDate && !dateDraft.trim()}
              aria-label="Save date"
              className="shrink-0 rounded-md bg-brand-600 p-1.5 text-white hover:bg-brand-500 disabled:opacity-40"
            >
              <Check size={13} />
            </button>
            {task.deadlineHint && (
              <button
                onClick={() => {
                  updateTaskDeadline(task.id, '');
                  closeDateEdit();
                }}
                aria-label="Remove date"
                title="Remove date"
                className="shrink-0 rounded-md border border-red-200 dark:border-red-500/20 p-1.5 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/20"
              >
                <Trash2 size={13} />
              </button>
            )}
            <button
              onClick={closeDateEdit}
              aria-label="Cancel date edit"
              className="shrink-0 rounded-md border border-slate-200 dark:border-slate-800 p-1.5 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"
            >
              <X size={13} />
            </button>
          </div>

          {/* Free text — vagueness stays first-class */}
          <input
            type="text"
            value={dateDraft}
            onChange={(e) => setDateDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') saveDateEdit();
              if (e.key === 'Escape') closeDateEdit();
            }}
            aria-label="Or type your own"
            placeholder="…or type your own: 'after lunch', 'end of week'"
            className="w-full rounded-md border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-2 py-1.5 text-xs text-slate-900 dark:text-white placeholder-slate-500 focus:border-red-400 focus:outline-none"
          />
        </div>
      )}

      {/* Zone move picker: the user's judgment always overrides the AI's. */}
      <AnimatePresence initial={false}>
        {moveOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.15 }}
          >
            <div className="border-t border-slate-200 dark:border-slate-800 px-3 py-2">
              {/* Lift & shift (V2 §4e, register #30, Roxanne): undated tasks
                  can be hand-ordered within their zone. Dated tasks rank by
                  date, so the controls only appear when they can act. */}
              {/* 15 Oct 2026: every open task can be hand-ordered within its
                  group — same day, rough date, or undated (manualOrder.ts). */}
              {!task.done && (
                <>
                  <p className="mb-1.5 px-0.5 text-[10px] font-bold uppercase tracking-wider text-slate-500">
                    Order in zone <span className="normal-case font-medium">({orderGroupLabel(task)})</span>
                  </p>
                  {/* 15 Oct 2026 (founder): Move up/down switches the WHOLE board
                      to Date sort (liftShift) — the only sort where hand order
                      shows. That happened without a word; now the card says so
                      before the tap, and after it the Sort menu reads "Date". */}
                  {!inDateSort && (
                    <p className="-mt-0.5 mb-1.5 px-0.5 text-[11px] leading-snug text-slate-500 dark:text-slate-400">
                      Moving switches the board to <b>Sort: Date</b>, where your own order shows.
                    </p>
                  )}
                  <div className="mb-2 grid grid-cols-2 gap-1.5">
                    {/* Two pastels so the pair reads at a glance (Chad, 12
                        Aug: "I missed them completely"): mint = up, lavender
                        = down. */}
                    <button
                      onClick={() => liftShift(-1)}
                      disabled={!moves.up}
                      className="disabled:cursor-not-allowed disabled:opacity-40 flex items-center justify-center gap-1.5 rounded-lg border border-emerald-200 dark:border-emerald-500/30 bg-emerald-50 dark:bg-emerald-950/30 px-2 py-2 text-[10px] font-bold text-emerald-700 dark:text-emerald-400 transition-colors hover:bg-emerald-100 dark:hover:bg-emerald-900/40"
                    >
                      <ChevronUp size={12} /> Move up
                    </button>
                    <button
                      onClick={() => liftShift(1)}
                      disabled={!moves.down}
                      className="disabled:cursor-not-allowed disabled:opacity-40 flex items-center justify-center gap-1.5 rounded-lg border border-indigo-200 dark:border-indigo-500/30 bg-indigo-50 dark:bg-indigo-950/30 px-2 py-2 text-[10px] font-bold text-indigo-700 dark:text-indigo-400 transition-colors hover:bg-indigo-100 dark:hover:bg-indigo-900/40"
                    >
                      <ChevronDown size={12} /> Move down
                    </button>
                  </div>
                  {moves.reason && (
                    <p role="status" className="-mt-1 mb-2 px-0.5 text-[11px] leading-snug text-slate-500 dark:text-slate-400">
                      {moves.reason}
                    </p>
                  )}
                </>
              )}
              <p className="mb-1.5 px-0.5 text-[10px] font-bold uppercase tracking-wider text-slate-500">
                Move to
              </p>
              <div className="grid grid-cols-3 gap-1.5">
                {ZONES.filter((z) => z.id !== task.zone).map((z) => (
                  <button
                    key={z.id}
                    onClick={() => {
                      moveTask(task.id, z.id);
                      setMoveOpen(false);
                    }}
                    className={`flex items-center justify-center gap-1.5 rounded-lg border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950 px-2 py-2 text-[10px] font-bold transition-colors hover:bg-slate-100 dark:hover:bg-slate-800 ${z.color.text}`}
                  >
                    <ZoneIcon zone={z.id} size={12} className={z.color.text} />
                    {z.id.replace('zone', 'Zone ')}
                  </button>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {expanding && (
        <div className="flex items-center gap-2 px-4 pb-3 text-xs text-slate-500 dark:text-slate-400">
          <Loader2 size={14} className="animate-spin text-brand-600 dark:text-brand-400" />
          {updateBusy
            ? 'Merging your update into the plan — completed steps stay untouched…'
            : 'Building your step-by-step roadmap…'}
        </div>
      )}

      <AnimatePresence initial={false}>
        {expanded && !expanding && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <div className="space-y-1.5 border-t border-slate-200 dark:border-slate-800 px-4 py-3">
              {task.steps.map((step, i) =>
                editingStepId === step.id ? (
                  /* Edit mode: text field + save/cancel, reorder, delete */
                  <div
                    key={step.id}
                    className="space-y-1.5 rounded-lg border border-brand-300 dark:border-brand-500/40 bg-slate-50 dark:bg-slate-950 p-2"
                  >
                    <input
                      type="text"
                      value={stepDraft}
                      onChange={(e) => setStepDraft(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && saveStepEdit()}
                      autoFocus
                      className="w-full rounded-md border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-2 py-1.5 text-xs text-slate-900 dark:text-white focus:border-brand-500 focus:outline-none"
                    />
                    <div className="flex items-center gap-1">
                      <button
                        onClick={saveStepEdit}
                        aria-label="Save step"
                        className="rounded-md bg-brand-600 p-1.5 text-white hover:bg-brand-500"
                      >
                        <Check size={13} />
                      </button>
                      <button
                        onClick={() => setEditingStepId(null)}
                        aria-label="Cancel"
                        className="rounded-md border border-slate-200 dark:border-slate-800 p-1.5 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"
                      >
                        <X size={13} />
                      </button>
                      <span className="mx-1 h-4 w-px bg-slate-200 dark:bg-slate-800" />
                      <button
                        onClick={() => moveStep(task.id, step.id, -1)}
                        disabled={i === 0}
                        aria-label="Move step up"
                        className="rounded-md border border-slate-200 dark:border-slate-800 p-1.5 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-30"
                      >
                        <ChevronUp size={13} />
                      </button>
                      <button
                        onClick={() => moveStep(task.id, step.id, 1)}
                        disabled={i === task.steps.length - 1}
                        aria-label="Move step down"
                        className="rounded-md border border-slate-200 dark:border-slate-800 p-1.5 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-30"
                      >
                        <ChevronDown size={13} />
                      </button>
                      <button
                        onClick={() => {
                          promoteStep(task.id, step.id);
                          setEditingStepId(null);
                        }}
                        aria-label="Make this its own task"
                        title="Make this its own task"
                        className="rounded-md border border-brand-200 dark:border-brand-500/30 p-1.5 text-brand-600 dark:text-brand-400 hover:bg-brand-50 dark:hover:bg-brand-900/20"
                      >
                        <ArrowUpRight size={13} />
                      </button>
                      <button
                        onClick={() => {
                          deleteStep(task.id, step.id);
                          setEditingStepId(null);
                        }}
                        aria-label="Delete step"
                        className="ml-auto rounded-md border border-red-200 dark:border-red-500/20 p-1.5 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/20"
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </div>
                ) : (
                  /* View mode */
                  <div key={step.id} className="group flex items-start gap-1">
                    <button
                      onClick={() => cycleStepState(task.id, step.id)}
                      title="Tap: not started → in progress → done"
                      className="flex min-w-0 flex-1 items-start gap-2 rounded-lg p-1.5 text-left transition-colors hover:bg-slate-100 dark:hover:bg-slate-950/60"
                    >
                      <span className="mt-0.5 shrink-0 text-slate-400 dark:text-slate-600">
                        <ProgressCircle progress={stepProgress(step)} done={step.done} size={15} />
                      </span>
                      <span
                        className={`flex-1 text-xs leading-relaxed ${
                          step.done ? 'text-slate-500 line-through' : 'text-slate-600 dark:text-slate-300'
                        }`}
                      >
                        <span className="font-semibold text-slate-500">Step {i + 1}:</span>{' '}
                        {step.text}{' '}
                        <span className="whitespace-nowrap text-[11px] text-slate-500">
                          ({step.minutes} min)
                        </span>
                      </span>
                    </button>
                    <button
                      onClick={() => {
                        setEditingStepId(step.id);
                        setStepDraft(`${step.text} ${step.minutes} min`);
                      }}
                      aria-label={`Edit step ${i + 1}`}
                      className="mt-1 shrink-0 rounded-md p-1 text-slate-400 dark:text-slate-600 transition-colors hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-brand-600 dark:hover:text-brand-400"
                    >
                      <Pencil size={12} />
                    </button>
                  </div>
                ),
              )}

              {/* Working note — the parking space for "where I left off",
                  so switching tasks mid-day costs nothing. Always free. */}
              {noteOpen ? (
                <div className="space-y-1.5 rounded-lg border border-amber-200 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-950/20 p-2">
                  <textarea
                    value={noteDraft}
                    onChange={(e) => setNoteDraft(e.target.value)}
                    rows={2}
                    autoFocus
                    placeholder="Where did you leave off? e.g. 'Waiting on Mark's reply — resume at step 3.'"
                    className="w-full resize-none rounded-md border border-amber-200 dark:border-amber-500/20 bg-white dark:bg-slate-900 px-2 py-1.5 text-xs text-slate-900 dark:text-white placeholder-slate-500 focus:border-amber-400 focus:outline-none"
                  />
                  <div className="flex items-center gap-1.5">
                    <button
                      onClick={() => {
                        updateTaskNote(task.id, noteDraft);
                        setNoteOpen(false);
                      }}
                      aria-label="Save note"
                      className="rounded-md bg-amber-500 p-1.5 text-white hover:bg-amber-400"
                    >
                      <Check size={13} />
                    </button>
                    {task.note && (
                      <button
                        onClick={() => {
                          updateTaskNote(task.id, '');
                          setNoteOpen(false);
                        }}
                        aria-label="Delete note"
                        className="rounded-md border border-red-200 dark:border-red-500/20 p-1.5 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/20"
                      >
                        <Trash2 size={13} />
                      </button>
                    )}
                    <button
                      onClick={() => setNoteOpen(false)}
                      aria-label="Cancel note"
                      className="rounded-md border border-slate-200 dark:border-slate-800 p-1.5 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"
                    >
                      <X size={13} />
                    </button>
                  </div>
                </div>
              ) : task.note ? (
                <button
                  onClick={() => {
                    setNoteDraft(task.note ?? '');
                    setNoteOpen(true);
                  }}
                  title="Edit note"
                  className="flex w-full items-start gap-2 rounded-lg border border-amber-200 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-950/20 p-2 text-left transition-colors hover:bg-amber-100 dark:hover:bg-amber-950/40"
                >
                  <StickyNote size={13} className="mt-0.5 shrink-0 text-amber-500" />
                  <span className="flex-1 whitespace-pre-wrap text-xs leading-relaxed text-slate-700 dark:text-slate-300">
                    {task.note}
                  </span>
                </button>
              ) : (
                <button
                  onClick={() => {
                    setNoteDraft('');
                    setNoteOpen(true);
                  }}
                  className="flex items-center gap-1 text-[11px] font-medium text-slate-400 dark:text-slate-500 hover:text-amber-600 dark:hover:text-amber-400"
                >
                  <StickyNote size={11} /> Add a note
                </button>
              )}

              {/* Tags: project / role / deliverable grouping (Phase D) */}
              {tagsOpen ? (
                <div ref={tagsRef} className="space-y-2 rounded-lg border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950 p-2">
                  {Object.values(allTags).length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      {Object.values(allTags)
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .map((tag) => {
                          const on = (task.tags ?? []).includes(tag.id);
                          return (
                            <span key={tag.id} className="inline-flex items-center">
                            <button
                              onClick={() => toggleTaskTag(task.id, tag.id)}
                              className={`inline-flex items-center gap-1 rounded-full border px-2 py-1 text-[11px] font-bold transition-all ${on ? '' : 'opacity-50 hover:opacity-80'}`}
                              style={{
                                color: tag.color,
                                borderColor: on ? tag.color : `${tag.color}55`,
                                backgroundColor: on ? `${tag.color}22` : 'transparent',
                              }}
                            >
                              <span
                                className="h-1.5 w-1.5 rounded-full"
                                style={{ backgroundColor: tag.color }}
                              />
                              {tag.name}
                              {on && ' ✓'}
                            </button>
                            <CoreStar tagId={tag.id} />
                            </span>
                          );
                        })}
                    </div>
                  )}
                  <CoreGuidance />
                  {(() => {
                    const suggested = suggestTagName(task.text);
                    const exists =
                      suggested &&
                      Object.values(allTags).some(
                        (t) => t.name.toLowerCase() === suggested.toLowerCase(),
                      );
                    return suggested && !exists ? (
                      <button
                        onClick={() => {
                          const id = createTag(suggested, 'project');
                          if (id) toggleTaskTag(task.id, id);
                        }}
                        className="rounded-full border border-dashed border-brand-300 dark:border-brand-500/40 px-2 py-1 text-[11px] font-bold text-brand-600 dark:text-brand-400 hover:bg-brand-50 dark:hover:bg-brand-900/20"
                      >
                        + Tag as “{suggested}”
                      </button>
                    ) : null;
                  })()}
                  <div className="flex flex-wrap items-center gap-1.5">
                    <input
                      type="text"
                      value={newTagName}
                      onChange={(e) => setNewTagName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && newTagName.trim()) {
                          const id = createTag(newTagName, newTagKind);
                          if (id) toggleTaskTag(task.id, id);
                          setNewTagName('');
                        }
                        if (e.key === 'Escape') setTagsOpen(false);
                      }}
                      placeholder="New tag…"
                      aria-label="New tag name"
                      // Phones: the input gets its own full-width line and a
                      // 16px font (iOS zooms the page into any smaller input).
                      // 11 Oct 2026 — on a phone it shared the row with four
                      // buttons and showed only "Ne". sm+ keeps the one-row layout.
                      className="min-w-0 basis-full sm:basis-auto sm:flex-1 rounded-md border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-2 py-1.5 text-base sm:text-xs text-slate-900 dark:text-white placeholder-slate-500 focus:border-brand-500 focus:outline-none"
                    />
                    {TAG_KINDS.map((k) => (
                      <button
                        key={k.id}
                        onClick={() => setNewTagKind(k.id)}
                        className={`rounded-md border px-2 py-1.5 text-[10px] font-bold transition-colors ${
                          newTagKind === k.id
                            ? 'border-brand-400 bg-brand-600/10 text-brand-600 dark:text-brand-400'
                            : 'border-slate-200 dark:border-slate-800 text-slate-500'
                        }`}
                      >
                        {k.label}
                      </button>
                    ))}
                    <button
                      onClick={() => {
                        if (!newTagName.trim()) return;
                        const id = createTag(newTagName, newTagKind);
                        if (id) toggleTaskTag(task.id, id);
                        setNewTagName('');
                      }}
                      disabled={!newTagName.trim()}
                      aria-label="Create tag"
                      className="rounded-md bg-brand-600 p-1.5 text-white hover:bg-brand-500 disabled:opacity-40"
                    >
                      <Plus size={13} />
                    </button>
                    <button
                      onClick={() => setTagsOpen(false)}
                      aria-label="Close tags"
                      className="rounded-md border border-slate-200 dark:border-slate-800 p-1.5 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"
                    >
                      <X size={13} />
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => setTagsOpen(true)}
                  className="flex items-center gap-1 text-[11px] font-medium text-slate-400 dark:text-slate-500 hover:text-brand-600 dark:hover:text-brand-400"
                >
                  <TagIcon size={11} />{' '}
                  {(task.tags ?? []).length > 0 ? 'Edit tags' : 'Add to a project / role'}
                </button>
              )}

              {/* Weekly Insights (web only): Zone 3 hand-off + check-back. */}
              <HandOffPanel task={task} />

              {/* App Review 1.2: a way to report what the AI produced for
                  this task (its zone, its plan) — every user, every task. */}
              <ReportResultLink task={task} />

              {/* Composer: your own steps, typed or dictated — always free */}
              <div className="flex items-center gap-1.5 pt-1">
                <input
                  type="text"
                  value={newStep}
                  onChange={(e) => setNewStep(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && submitNewStep()}
                  placeholder={
                    task.steps.length === 0
                      ? "Write your first step… (tip: end with '15 min')"
                      : "Add your own step… (tip: end with '15 min')"
                  }
                  className="min-w-0 flex-1 rounded-lg border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950 px-2.5 py-1.5 text-xs text-slate-900 dark:text-white placeholder-slate-500 focus:border-brand-500 focus:outline-none"
                />
                {stepVoice.supported && (
                  <button
                    onClick={() => stepVoice.toggle(newStep)}
                    aria-label={stepVoice.listening ? 'Stop dictation' : 'Dictate step'}
                    className={`shrink-0 rounded-lg border p-1.5 transition-all active:scale-95 ${
                      stepVoice.listening
                        ? 'animate-pulse border-red-500 bg-red-600 text-white'
                        : 'border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950 text-brand-600 dark:text-brand-400 hover:bg-slate-100 dark:hover:bg-slate-800'
                    }`}
                  >
                    {stepVoice.listening ? <MicOff size={13} /> : <Mic size={13} />}
                  </button>
                )}
                <button
                  onClick={submitNewStep}
                  disabled={!newStep.trim()}
                  aria-label="Add step"
                  className="shrink-0 rounded-lg bg-brand-600 p-1.5 text-white transition-colors hover:bg-brand-500 disabled:opacity-40"
                >
                  <Plus size={13} />
                </button>
              </div>
              {stepVoice.notice && (
                <p className="text-[10px] text-amber-600 dark:text-amber-400">🎤 {stepVoice.notice}</p>
              )}

              {premium && task.stepsGenerated && (
                <div className="mt-1 flex flex-wrap items-center gap-1.5">
                  <button
                    onClick={() => void addExtraStep(task.id)}
                    disabled={expanding}
                    className="flex items-center gap-1 rounded-lg border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950 px-2.5 py-1.5 text-[11px] font-bold text-brand-600 dark:text-brand-400 transition-colors hover:bg-slate-200 dark:hover:bg-slate-800 disabled:opacity-50"
                  >
                    <Plus size={12} /> Generate extra step (AI)
                  </button>
                  <button
                    onClick={() => setUpdateOpen((o) => !o)}
                    disabled={expanding}
                    aria-expanded={updateOpen}
                    className={`flex items-center gap-1 rounded-lg border px-2.5 py-1.5 text-[11px] font-bold transition-colors disabled:opacity-50 ${
                      updateOpen
                        ? 'border-brand-300 dark:border-brand-500/40 bg-brand-50 dark:bg-brand-900/20 text-brand-600 dark:text-brand-400'
                        : 'border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950 text-brand-600 dark:text-brand-400 hover:bg-slate-200 dark:hover:bg-slate-800'
                    }`}
                  >
                    <RefreshCw size={12} /> Update this plan (AI)
                  </button>
                </div>
              )}
              {planNotice && (
                <p role="status" className="text-[11px] text-amber-700 dark:text-amber-400">{planNotice}</p>
              )}

              {/* Living plans: tell the AI what changed; it reshapes the
                  remaining steps and never touches completed ones. */}
              {premium && updateOpen && !expanding && (
                <div className="space-y-1.5 rounded-lg border border-brand-200 dark:border-brand-500/30 bg-slate-50 dark:bg-slate-950 p-2">
                  <textarea
                    value={updateNote}
                    onChange={(e) => setUpdateNote(e.target.value)}
                    rows={2}
                    autoFocus
                    placeholder="What changed? e.g. 'Meeting moved to Thursday. Vendor said no — need a new supplier. Add a site visit.'"
                    className="w-full resize-none rounded-md border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-2 py-1.5 text-xs text-slate-900 dark:text-white placeholder-slate-500 focus:border-brand-500 focus:outline-none"
                  />
                  <div className="flex items-center gap-1.5">
                    <button
                      onClick={() => void submitPlanUpdate()}
                      disabled={!updateNote.trim()}
                      className="flex items-center gap-1 rounded-md bg-brand-600 px-2.5 py-1.5 text-[11px] font-bold text-white transition-colors hover:bg-brand-500 disabled:opacity-40"
                    >
                      <RefreshCw size={11} /> Merge update
                    </button>
                    {noteVoice.supported && (
                      <button
                        onClick={() => noteVoice.toggle(updateNote)}
                        aria-label={noteVoice.listening ? 'Stop dictation' : 'Dictate update'}
                        className={`rounded-md border p-1.5 transition-all active:scale-95 ${
                          noteVoice.listening
                            ? 'animate-pulse border-red-500 bg-red-600 text-white'
                            : 'border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 text-brand-600 dark:text-brand-400 hover:bg-slate-100 dark:hover:bg-slate-800'
                        }`}
                      >
                        {noteVoice.listening ? <MicOff size={12} /> : <Mic size={12} />}
                      </button>
                    )}
                    <button
                      onClick={() => {
                        setUpdateOpen(false);
                        setUpdateNote('');
                      }}
                      aria-label="Cancel plan update"
                      className="rounded-md border border-slate-200 dark:border-slate-800 p-1.5 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"
                    >
                      <X size={12} />
                    </button>
                    <span className="ml-auto text-[10px] text-slate-500">
                      Done steps are never rewritten
                    </span>
                  </div>
                  {noteVoice.notice && (
                    <p className="text-[10px] text-amber-600 dark:text-amber-400">
                      🎤 {noteVoice.notice}
                    </p>
                  )}
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
});
